import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  Label,
} from "@jourfx/ui";
import { AlertTriangle } from "lucide-react";

interface CancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  isWithinCutoff: boolean;
  isLoading?: boolean;
}

const cancelReasons = [
  { value: "illness", label: "Krankheit" },
  { value: "conflict", label: "Terminkollision" },
  { value: "personal", label: "Persönliche Gründe" },
  { value: "other", label: "Sonstiges" },
];

export function CancelDialog({
  open,
  onOpenChange,
  onConfirm,
  isWithinCutoff,
  isLoading,
}: CancelDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState("");

  const handleConfirm = () => {
    const reason = selectedReason === "other" ? otherReason : selectedReason;
    onConfirm(reason);
  };

  if (isWithinCutoff) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Absage nicht möglich
            </SheetTitle>
            <SheetDescription>
              Termine können nur bis 48 Stunden vorher online abgesagt werden.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="font-medium text-amber-800 mb-2">
              Kurzfristige Absagen werden intern bearbeitet.
            </p>
            <p className="text-sm text-amber-700">
              Sie können den Termin jetzt nicht online absagen. Nutzen Sie bei Bedarf den Rückrufwunsch im Portal.
            </p>
          </div>

          <div className="mt-6">
            <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-white text-[var(--stone-800)] rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Termin absagen</SheetTitle>
          <SheetDescription>
            Möchten Sie diesen Termin wirklich absagen?
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-sm font-medium">Grund der Absage</Label>
            <div className="mt-2 space-y-2">
              {cancelReasons.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedReason === reason.value
                      ? "border-sage-500 bg-sage-50"
                      : "border-[--client-border] hover:border-sage-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedReason === reason.value
                        ? "border-sage-600"
                        : "border-gray-300"
                    }`}
                  >
                    {selectedReason === reason.value && (
                      <div className="w-2 h-2 rounded-full bg-sage-600" />
                    )}
                  </div>
                  <span>{reason.label}</span>
                </label>
              ))}
            </div>
          </div>

          {selectedReason === "other" && (
            <div>
              <Label htmlFor="other-reason">Bitte beschreiben</Label>
              <textarea
                id="other-reason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Grund eingeben..."
                className="mt-1 w-full p-3 border border-[--client-border] rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-sage-500"
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={!selectedReason || (selectedReason === "other" && !otherReason) || isLoading}
          >
            {isLoading ? "Wird abgesagt..." : "Termin absagen"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
